import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import { toast, Toaster } from 'sonner';
import { useNotifications } from '../hooks/use-notifications';

type Severity = 'success' | 'error' | 'warning' | 'info';

interface NotificationContextType {
  showNotification: (message: string, severity?: Severity) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const showNotification = useCallback((message: string, severity: Severity = 'success') => {
    switch (severity) {
      case 'success':
        toast.success(message);
        break;
      case 'error':
        toast.error(message);
        break;
      case 'warning':
        toast.warning(message);
        break;
      case 'info':
        toast.info(message);
        break;
      default:
        toast(message);
    }
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      <>
        <TitleUpdater />
        {children}
      </>
      <Toaster
        position="bottom-right"
        style={{ zIndex: 9999 }}
        toastOptions={{
          duration: 4000,
          classNames: {
            toast: 'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
            success: 'group-[.toast]:bg-green-50 group-[.toast]:text-green-900 group-[.toast]:border-green-200 dark:group-[.toast]:bg-green-900/20 dark:group-[.toast]:text-green-100 dark:group-[.toast]:border-green-800',
            error: 'group-[.toast]:bg-red-50 group-[.toast]:text-red-900 group-[.toast]:border-red-200 dark:group-[.toast]:bg-red-900/20 dark:group-[.toast]:text-red-100 dark:group-[.toast]:border-red-800',
            warning: 'group-[.toast]:bg-amber-50 group-[.toast]:text-amber-900 group-[.toast]:border-amber-200 dark:group-[.toast]:bg-amber-900/20 dark:group-[.toast]:text-amber-100 dark:group-[.toast]:border-amber-800',
            info: 'group-[.toast]:bg-blue-50 group-[.toast]:text-blue-900 group-[.toast]:border-blue-200 dark:group-[.toast]:bg-blue-900/20 dark:group-[.toast]:text-blue-100 dark:group-[.toast]:border-blue-800',
          },
        }}
        richColors
        closeButton
      />
    </NotificationContext.Provider>
  );
};

// Update document.title with unread count
const TitleUpdater: React.FC = () => {
  const { unreadCount } = useNotifications();
  const baseTitleRef = useRef<string>(typeof document !== 'undefined' ? document.title : 'Timesheet App');

  useEffect(() => {
    const base = baseTitleRef.current || 'Timesheet App';
    if (unreadCount > 0) {
      document.title = `${base} (${unreadCount})`;
    } else {
      document.title = base;
    }
  }, [unreadCount]);

  return null;
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// Direct toast export for cases where hook isn't needed
export { toast };
